import { type ComponentPropsWithoutRef, type ComponentRef, forwardRef } from 'react'

import * as SwitchPrimitives from '@radix-ui/react-switch'
import { useControllableState } from '@radix-ui/react-use-controllable-state'

import { cn } from '@/lib/utils'

const isChecked = (props: Record<string, unknown>) =>
  props['data-state'] === 'checked' || props['data-state'] === 'open'

export const Switch = forwardRef<
  ComponentRef<typeof SwitchPrimitives.Root>,
  ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ checked, onCheckedChange: onCheckedChangeProps, ...props }, ref) => {
  const [ck, onCheckedChange] = useControllableState({
    prop: checked,
    defaultProp: isChecked(props),
    onChange: onCheckedChangeProps,
    caller: '@valian/Switch',
  })

  return <UncontrolledSwitch checked={ck} onCheckedChange={onCheckedChange} {...props} ref={ref} />
})

export const UncontrolledSwitch = forwardRef<
  ComponentRef<typeof SwitchPrimitives.Root>,
  ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, checked, onCheckedChange: onCheckedChangeProps, ...props }, ref) => (
  <SwitchPrimitives.Root
    checked={checked}
    className={cn(
      'group relative inline-flex h-6 w-10 shrink-0 cursor-pointer items-center rounded-full border focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:ring-offset-1 focus-visible:ring-offset-white focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',

      'data-[state=checked]:border-blue-700 data-[state=checked]:bg-blue-500 data-[state=checked]:hover:border-blue-800 data-[state=checked]:hover:bg-blue-700',
      'data-[state=open]:border-blue-700 data-[state=open]:bg-blue-500 data-[state=open]:hover:border-blue-800 data-[state=open]:hover:bg-blue-700',

      'data-[state=unchecked]:border-gray-300 data-[state=unchecked]:bg-gray-100 data-[state=unchecked]:hover:border-gray-400',
      'data-[state=close]:border-gray-300 data-[state=close]:bg-gray-100 data-[state=close]:hover:border-gray-400',
      className
    )}
    onCheckedChange={onCheckedChangeProps}
    {...props}
    ref={ref}>
    <SwitchPrimitives.Thumb
      className={cn(
        'pointer-events-none box-content block h-5 w-5 rounded-full border bg-white ring-0 transition-transform data-[state=checked]:translate-x-[17px]',

        'data-[state=checked]:border-blue-700',
        'data-[state=open]:border-blue-700',

        'data-[state=unchecked]:-translate-x-px data-[state=unchecked]:border-gray-300 group-hover:data-[state=unchecked]:border-gray-400 group-hover:data-[state=unchecked]:bg-gray-100',
        'data-[state=close]:-translate-x-px data-[state=close]:border-gray-300 group-hover:data-[state=close]:border-gray-400 group-hover:data-[state=close]:bg-gray-100'
      )}
    />
  </SwitchPrimitives.Root>
))
